/* eslint-disable ember/no-computed-properties-in-native-classes */
import { not, and, notEmpty } from '@ember/object/computed';
import { service } from '@ember/service';
import Model, { attr, belongsTo, type AsyncBelongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import type LocationModel from 'garaje/models/location';
import type VisitorEntranceModel from 'garaje/models/visitor-entrance';
import type DeviceStatusService from 'garaje/services/device-status-service';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import deviceStatus from 'garaje/utils/decorators/device-status';

import type AbstractPrinter from './abstract/printer';
import type ZoneModel from './zone';

@deviceStatus
class DeviceModel extends Model {
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service('device-status-service') declare deviceStatusService: DeviceStatusService;
  @belongsTo('location', { async: true, inverse: 'devices' }) declare location: AsyncBelongsTo<LocationModel>;
  @belongsTo('abstract/printer', { async: true, inverse: 'devices', polymorphic: true }) declare printer:
    | AsyncBelongsTo<AbstractPrinter>
    | AbstractPrinter;
  @belongsTo('abstract/printer', { async: true, inverse: null, polymorphic: true }) declare failoverPrinter:
    | AsyncBelongsTo<AbstractPrinter>
    | AbstractPrinter
    | null;
  @belongsTo('visitor-entrance', { async: true, inverse: 'devices' })
  declare visitorEntrance: AsyncBelongsTo<VisitorEntranceModel>;
  @belongsTo('zone', { async: true, inverse: 'devices' }) declare zone: AsyncBelongsTo<ZoneModel>;

  @attr('string') declare userToken: string;
  @attr('boolean') declare isConnected: boolean;
  @attr('date') declare grantExpiresAt: Date;
  @attr('string') declare osVersion: string;
  @attr('string') declare maximumOsVersion: string;
  @attr('string') declare appVersion: string;
  @attr('string') declare currentIpadAppVersion: string;
  @attr('string') declare ipAddress: string;
  @attr('string') declare wifiNetwork: string;
  @attr('string') declare printerStatus: string;
  @attr('string') declare failoverPrinterStatus: string;
  @attr('date') declare lastHeartbeatAt: Date;
  @attr('boolean') declare isSleeping: boolean;
  @attr('string') declare deviceType: string;
  @attr('string') declare name: string;
  @attr('string') declare alias: string | null;

  @tracked newPingRequestWentOutAt: Date | null = null;
  @tracked oldPingRequestWentOutAt: Date | null = null;
  @not('isDeleted') declare isNotDeleted: boolean;
  @and('isConnected', 'isNotDeleted') declare isVisible: boolean;
  @notEmpty('deviceType') declare hasType: boolean;
  // Provided by the @deviceStatus decorator (app/utils/decorators/device-status.js).
  // True iff `lastHeartbeatAt` is within IPAD_LAST_SEEN_THRESHOLD (or the
  // DeviceStatusService reports CONNECTED when the `devices-service-status-api`
  // flag is on). Declared here so callers don't need local intersection casts.
  declare isIpadSeenRecently: boolean;

  get displayName(): string {
    if (this.alias) {
      return `${this.alias} (${this.name})`;
    }
    return this.name;
  }

  get isIpad(): boolean {
    if (!this.deviceType) return false;
    // x86_ covers Intel-based iOS simulators; arm64 covers Apple Silicon iOS simulators.
    return (
      this.deviceType.startsWith('iPad') || this.deviceType.startsWith('x86_') || this.deviceType.startsWith('arm64')
    );
  }

  get isSimulator(): boolean {
    if (!this.deviceType) return false;
    return this.deviceType.startsWith('x86_') || this.deviceType.startsWith('arm64');
  }

  get status(): string {
    if (!this.isConnected) return 'Disconnected';

    return this.isSleeping ? 'Sleeping' : 'Connected';
  }

  get locationName(): string {
    let returnValue = '';
    try {
      // @ts-expect-error - belongsTo method accepts string for relationship name
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const location = this.belongsTo('location').value() as LocationModel | null;
      if (location?.name) {
        returnValue = location.name;
      }
    } catch (_e) {
      // Location not loaded, leave as empty string
    }
    return returnValue;
  }

  setPingTime(): void {
    if (this.newPingRequestWentOutAt) {
      this.oldPingRequestWentOutAt = this.newPingRequestWentOutAt;
    }
    this.newPingRequestWentOutAt = new Date();
  }

  toDataForCsvExport(): Record<string, string> {
    return {
      Name: this.displayName || '',
      'IP Address': this.ipAddress || '',
      Location: this.locationName,
      Status: this.status,
      'Last Checked In': this.lastHeartbeatAt?.toISOString() || '',
      'App Version': this.appVersion || '',
    };
  }
}

export default DeviceModel;

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    device: DeviceModel;
  }
}
